import { Button, Form, Typography } from 'antd';
import { useContext, useEffect, useState } from 'react';
import { Field, GridRow } from '../../models';
import { apiClient, hasSetWorkflowStatusPermission } from '../../utils';
import { FieldFormItem } from './field-form-item';
import { UserContext } from '../contexts';

export function ChangeRequestForm(props: {
  gridId?: string;
  field?: Field;
  rowId?: string;
  onSaved: (row: GridRow) => void;
  hideLabel?: boolean;
  value?: any;
  canDoVt?: boolean;
  allowRecordWithErrors?: boolean;
  predictions?: { value: string; confidence: string }[];
  showPredictionError?: boolean;
}) {
  const { Text } = Typography;

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>();

  const [workflowPermission, setWorkflowPermission] = useState(false);
  const [nextStep, setNextStep] = useState('');
  const { user } = useContext(UserContext);

  useEffect(() => {
    if (props.field?.isWorkFlow && props.field?.picklistValues) {
      const sortedPicklistValues = props.field.picklistValues?.sort((a, b) => a.order - b.order);
      const currentIndex = sortedPicklistValues?.findIndex(pv => pv.id === props.value);

      const nextStep =
        currentIndex + 1 < sortedPicklistValues?.length
          ? sortedPicklistValues?.[currentIndex + 1].val
          : sortedPicklistValues?.[currentIndex].val;

      setNextStep(nextStep);
    }
  }, [props.field, props.value]);

  useEffect(() => {
    if (hasSetWorkflowStatusPermission(user, props.field)) {
      setWorkflowPermission(true);
    } else {
      setWorkflowPermission(false);
    }
  }, [props.field, user]);

  const onFinish = (values: any) => {
    setLoading(true);
    setErrors(undefined);
    const fields = [];
    for (let key in values) {
      if (key.includes('_templateValues')) {
        fields.forEach((item, index) => {
          if (item.fieldId === key.split('_templateValues')[0]) {
            item.templateValue = values[key];
          }
        });
        continue;
      }
      if (key.includes('_uom')) {
        fields.forEach((item, index) => {
          if (item.fieldId === key.split('_uom')[0]) {
            item.value = item.value + '###' + values[key];
          }
        });
        continue;
      }

      let value = values[key];
      if (value != null && value !== undefined) {
        if (props.field?.dataType === 'DATE') {
          value = value.format('YYYY-MM-DD');
        } else if (props.field?.dataType === 'DATETIME') {
          value = value.format('YYYY-MM-DD HH:mm');
        } else if (props.field?.dataType === 'TIME') {
          value = value.format('HH:mm');
        } else if (props.field?.dataType === 'MULTIPICKLIST') {
          value = value.join('###');
        } else if (props.field?.dataType === 'MULTIFOREIGNKEY') {
          value = value.join('###');
        }else if ((props.field?.dataType === 'STRING' || props.field?.dataType === 'TEXT') && props.field?.isRichTextEditor) {
          if (value === '<p><br></p>') {
            value = undefined;
          }
        }
      }
      fields.push({ fieldId: key, value: value === undefined ? null : value, templateValue: '' });
    }
    let body: any = {
      gridId: props.gridId,
      rowId: props.rowId,
      fields,
      returnTasks: true,
    };

    if (fields?.length === 1) {
      if (props.predictions) {
        body = {
          ...body,
          predictions: JSON.stringify(props.predictions),
        };
      }
    }

    apiClient
      .put(`records/records`, body)
      .then((response: any) => {
        props.onSaved(response.data as GridRow);
        form.resetFields();
      })
      .catch((reason: any) => {
        if (reason?.response?.data?.errors?.length && reason?.response?.data?.errors[0].errors?.length) {
          setErrors(reason.response.data.errors[0].errors as string[]);
        } else if (reason?.response?.data?.message) {
          setErrors([reason.response.data.message]);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if ((props.field?.dataType === 'MULTIPICKLIST' || props.field?.dataType === 'MULTIFOREIGNKEY') && !!props.value) {
      const fieldsValue: any = {};
      fieldsValue[props.field?.id] = (props.value as string).split('###').filter(v => !v.startsWith('!!!'));
      form.setFieldsValue(fieldsValue);
    }
  }, [form, props.field, props.value]);

  if (props.field?.dataType === 'DYNAMIC') {
    return <Text type="secondary">Otomatik veri elemanı için değer önerisi yapılamaz.</Text>;
  }

  if (props.field?.dataType === 'RULEBASED') {
    return <Text type="secondary">Koşullu otomatik veri elemanı için değer önerisi yapılamaz.</Text>;
  }

  if (props.field?.dataType === 'FOREIGNKEY' && !!props.field?.mainForeignKeyFieldId) {
    return <Text type="secondary">Ek referans veri elemanı için değer önerisi yapılamaz.</Text>;
  }

  if (props.field?.dataType === 'LOOKUP') {
    return <Text type="secondary">Arama veri elemanı için değer önerisi yapılamaz.</Text>;
  }

  return (
    <Form form={form} name="basic" onFinish={onFinish} layout="vertical">
      <FieldFormItem
        form={form}
        gridId={props.gridId}
        field={props.field}
        recordId={props.rowId}
        hideLabel={props.hideLabel}
        allowRecordWithErrors={props.allowRecordWithErrors}
        value={props.value}
        predictions={props.predictions}
        showPredictionError={props.showPredictionError}
        layout="vertical"
      />
      {errors && (
        <Form.Item>
          <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {errors?.map((ve, i) => (
              <li key={i}>
                <Text type="danger">{ve}</Text>
              </li>
            ))}
          </ul>
        </Form.Item>
      )}

      <Form.Item style={{ marginBottom: 0 }}>
        <Button type="primary" htmlType="submit" loading={loading} style={{ width: '100%' }}>
          {!props.field?.isWorkFlow && `${props.canDoVt ? 'Kaydet' : 'Onaya gönder'}`}
          {props.field?.isWorkFlow && workflowPermission ? 'Kaydet' : nextStep}
        </Button>
      </Form.Item>
    </Form>
  );
}
